import {useEffect, useState} from "react";
import {Box, Collapse, IconButton, Tooltip, Typography, useTheme} from "@mui/material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

const keyOf = (c) => `${c.claimType}::${c.claimValue}`;

// Effective-permissions view/editor for the View/Edit User page (TMMFX-2164).
// Categories are collapsible (collapsed by default in view, expanded in edit). Each permission is a
// pill in one of three states: granted by group(s) (source dots + lock, not removable), individually
// added (plain pill, removable in edit), or not granted (dashed/faded, addable in edit; hidden in view).
//
// Props:
//   assignedGroups:   [{id, name, color, claims: [{claimType, claimValue, ...}]}]
//   individualClaims: [{claimType, claimValue}]  (granted directly, on top of groups)
//   allClaims:        [{claimType, claimValue, displayName, categoryId}]  (full catalog)
//   categories:       [{id, displayName, displayOrder}]
//   editMode, onAdd(claim), onRemove(claim)
const PermissionsManager = ({
    assignedGroups = [],
    individualClaims = [],
    allClaims = [],
    categories = [],
    editMode = false,
    onAdd,
    onRemove,
}) => {
    const theme = useTheme();
    const [openCats, setOpenCats] = useState({});

    // Reset expansion on mode switch (collapsed in view, expanded in edit — via the `?? editMode` default).
    useEffect(() => {
        setOpenCats({});
    }, [editMode]);

    const individualSet = new Set(individualClaims.map(keyOf));
    const groupClaimSets = assignedGroups.map((g) => ({group: g, set: new Set((g.claims ?? []).map(keyOf))}));

    const stateOf = (key) => {
        const sourceGroups = groupClaimSets.filter((x) => x.set.has(key)).map((x) => x.group);
        const individual = individualSet.has(key);
        return {sourceGroups, granted: sourceGroups.length > 0 || individual, individualOnly: individual && sourceGroups.length === 0};
    };

    const toggle = (id) => setOpenCats((s) => ({...s, [id]: !s[id]}));
    const cats = [...categories].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

    return (
        <Box>
            {assignedGroups.length > 0 && (
                <Box sx={{display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5}}>
                    {assignedGroups.map((g) => (
                        <Box key={g.id} sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                            <Box sx={{width: 10, height: 10, borderRadius: "50%", backgroundColor: g.color}} />
                            <Typography sx={{fontSize: "0.78rem"}}>{g.name}</Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {cats.map((cat) => {
                const catClaims = allClaims.filter((c) => c.categoryId === cat.id);
                const decorated = catClaims.map((c) => ({claim: c, st: stateOf(keyOf(c))}));
                const grantedCount = decorated.filter((d) => d.st.granted).length;

                if (!editMode && grantedCount === 0) return null;
                const visible = editMode ? decorated : decorated.filter((d) => d.st.granted);

                const open = openCats[cat.id] ?? editMode;
                const allOn = grantedCount === catClaims.length && catClaims.length > 0;

                return (
                    <Box key={cat.id} sx={{border: `1px solid ${theme.palette.divider}`, borderRadius: "6px", mb: 1, overflow: "hidden"}}>
                        <Box
                            onClick={() => toggle(cat.id)}
                            sx={{display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, cursor: "pointer", backgroundColor: theme.palette.background.secondary}}
                        >
                            {open ? <KeyboardArrowDownOutlinedIcon fontSize="small" /> : <KeyboardArrowRightOutlinedIcon fontSize="small" />}
                            <Typography sx={{fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em"}}>
                                {cat.displayName}
                            </Typography>
                            <Typography sx={{ml: "auto", fontSize: "0.78rem", fontWeight: 700, color: allOn ? "success.main" : "text.secondary"}}>
                                {grantedCount}/{catClaims.length}
                            </Typography>
                        </Box>
                        <Collapse in={open}>
                            <Box sx={{p: 1.5, display: "flex", flexWrap: "wrap", gap: 0.75}}>
                                {visible.map(({claim, st}) => (
                                    <PermPill key={keyOf(claim)} claim={claim} st={st} editMode={editMode} onAdd={onAdd} onRemove={onRemove} />
                                ))}
                            </Box>
                        </Collapse>
                    </Box>
                );
            })}
        </Box>
    );
};

const PermPill = ({claim, st, editMode, onAdd, onRemove}) => {
    const theme = useTheme();
    const {granted, sourceGroups, individualOnly} = st;

    const pillSx = {
        display: "inline-flex", alignItems: "center", gap: 0.5,
        px: 1, py: 0.25, borderRadius: "999px", fontSize: "0.8rem",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: granted ? theme.palette.background.secondary : "transparent",
        borderStyle: granted ? "solid" : "dashed",
        color: granted ? "text.primary" : "text.secondary",
        opacity: granted ? 1 : 0.6,
    };

    return (
        <Box sx={pillSx}>
            {sourceGroups.length > 0 && (
                <Box sx={{display: "flex", gap: 0.3}}>
                    {sourceGroups.map((g) => (
                        <Box key={g.id} title={g.name} sx={{width: 8, height: 8, borderRadius: "50%", backgroundColor: g.color, flex: "0 0 8px"}} />
                    ))}
                </Box>
            )}
            <span>{claim.displayName || claim.claimValue}</span>

            {editMode && sourceGroups.length > 0 && (
                <Tooltip title="Granted by group — manage on the Groups page or remove the group">
                    <LockOutlinedIcon sx={{fontSize: 13, color: "text.secondary", opacity: 0.6}} />
                </Tooltip>
            )}
            {editMode && individualOnly && (
                <Tooltip title="Remove this individual permission">
                    <IconButton size="small" sx={{p: 0.1, ml: 0.25}} onClick={() => onRemove(claim)}>
                        <CloseIcon sx={{fontSize: 14, color: "error.main"}} />
                    </IconButton>
                </Tooltip>
            )}
            {editMode && !granted && (
                <Tooltip title="Add this permission individually">
                    <IconButton size="small" sx={{p: 0.1, ml: 0.25}} onClick={() => onAdd(claim)}>
                        <AddIcon sx={{fontSize: 14, color: "secondary.main"}} />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

export default PermissionsManager;
